import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useAuth, canManageOrg } from "./auth-context";
import type { Organization } from "./types";

type OrgContextValue = {
  orgId: string;
  organization: Organization | null;
  role: Organization["role"] | null;
  canManage: boolean;
};

const OrgContext = createContext<OrgContextValue | null>(null);

/** Resolves the signed-in user's organization from the session (one org per user). */
export function OrgProvider({ children }: { children: ReactNode }) {
  const { organizations } = useAuth();
  const organization = organizations[0] ?? null;
  const orgId = organization?.id ?? "";

  const value = useMemo(
    () => ({
      orgId,
      organization,
      role: organization?.role ?? null,
      canManage: canManageOrg(organization?.role),
    }),
    [orgId, organization],
  );

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrg() {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error("useOrg must be used within OrgProvider");
  return ctx;
}
